import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ChevronLeft, ChevronRight, Wifi, Calendar } from "lucide-react";

type Slot = { start_at: string; end_at: string };
type Studio = { id: string; name: string; slug: string };

const startOfWeek = (d: Date) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};

const HOURS = Array.from({ length: 18 }, (_, i) => i + 6);

const Availability = () => {
  const [studios, setStudios] = useState<Studio[]>([]);
  const [studioId, setStudioId] = useState<string>("");
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek(new Date()));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);

  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  useEffect(() => {
    supabase.from("studios").select("id,name,slug").eq("active", true).order("display_order")
      .then(({ data }) => {
        const list = (data ?? []) as Studio[];
        setStudios(list);
        if (list.length && !studioId) setStudioId(list[0].id);
      });
  }, []);

  const load = async () => {
    if (!studioId) return;
    setLoading(true);
    const from = weekStart.toISOString();
    const toDate = new Date(weekStart); toDate.setDate(toDate.getDate() + 7);
    const { data } = await supabase.rpc("get_blocked_slots_v2" as any, { _studio_id: studioId, _from: from, _to: toDate.toISOString() });
    setSlots(((data ?? []) as any[]).map((r: any) => ({ start_at: r.start_at, end_at: r.end_at })));
    setLoading(false);
  };

  useEffect(() => { load(); }, [studioId, weekStart]);

  useEffect(() => {
    if (!studioId) return;
    // Realtime broadcasting of booking/slot_lock rows is disabled for privacy;
    // poll the safe `get_blocked_slots_v2` summary instead. Also refetch on focus.
    setLive(true);
    const id = window.setInterval(() => { load(); }, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { setLive(false); window.clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [studioId]);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  });

  const isBlocked = (day: Date, hour: number) => {
    const cellStart = new Date(day); cellStart.setHours(hour, 0, 0, 0);
    const cellEnd = new Date(day); cellEnd.setHours(hour + 1, 0, 0, 0);
    return slots.some((s) => new Date(s.start_at) < cellEnd && new Date(s.end_at) > cellStart);
  };

  const slug = studios.find((s) => s.id === studioId)?.slug;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="mb-10">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-2">LIVE AVAILABILITY</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-widest mb-3">BOOK A STUDIO</h1>
          <p className="font-body text-muted-foreground max-w-2xl">
            See real-time openings across our studios. Greyed cells are already booked or blocked —
            slots update instantly when payments or refunds clear.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Select value={studioId} onValueChange={setStudioId}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Studio…" /></SelectTrigger>
            <SelectContent>
              {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="font-mono text-xs uppercase tracking-widest">
            {weekStart.toLocaleDateString()} – {new Date(weekStart.getTime() + 6 * 86400000).toLocaleDateString()}
          </span>
          <Button variant="outline" size="sm" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>Today</Button>
          <span className={`ml-auto flex items-center gap-2 text-xs font-mono ${live ? "text-primary" : "text-muted-foreground"}`}>
            <Wifi className="w-3 h-3" /> {live ? "LIVE" : "—"} · {tz}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="bg-panel border border-border overflow-x-auto">
            <div className="grid grid-cols-[60px_repeat(7,minmax(90px,1fr))] min-w-[700px]">
              <div className="border-b border-r border-border p-2 bg-background"></div>
              {days.map((d) => (
                <div key={d.toISOString()} className="border-b border-r border-border p-2 text-center bg-background">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                  <div className="font-display text-sm">{d.getDate()}</div>
                </div>
              ))}
              {HOURS.map((h) => (
                <Fragment key={`row-${h}`}>
                  <div className="border-b border-r border-border p-1 text-[10px] font-mono text-muted-foreground text-right pr-2">
                    {h.toString().padStart(2, "0")}:00
                  </div>
                  {days.map((d) => {
                    const blocked = isBlocked(d, h);
                    return (
                      <div
                        key={`${d.toISOString()}-${h}`}
                        className={`border-b border-r border-border h-10 ${blocked ? "bg-muted/60 cursor-not-allowed" : "hover:bg-primary/20 cursor-pointer"}`}
                        title={blocked ? "Unavailable" : "Available"}
                      />
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-6 text-xs font-mono">
          <div className="flex gap-4 text-muted-foreground">
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-muted/60 inline-block border border-border" /> Booked / blocked</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-primary/20 inline-block border border-border" /> Available</span>
          </div>
          {slug && (
            <Button asChild>
              <Link to={`/book?studio=${slug}`} className="gap-2"><Calendar className="w-4 h-4" /> Continue booking</Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Availability;